/* eslint-disable react/no-danger */
import { memo, useState, useRef, useEffect } from 'react';

import { FaPlay } from 'react-icons/fa';
import { FiRadio } from 'react-icons/fi';
import Skeleton from 'react-loading-skeleton';
import { useTranslation } from 'react-i18next';
import { UseQueryResult } from 'react-query';
import { AxiosError } from 'axios';
import { useHistory } from 'react-router-dom';

import { useEventfy } from '@hooks';
import { RoomsWithSpeakersResult, RoomData } from '@common/types/api';
import { formatRoomDisplayData } from '@utils';

import * as S from './styles';

type RoomStatus = 'live' | 'soon' | 'reprise';

const getRoomStatus = (room: RoomData): RoomStatus => {
  const { status } = formatRoomDisplayData(room);
  if (status === 'pages.rooms.soon') return 'soon';
  if (status === 'pages.rooms.reprise') return 'reprise';
  return 'live';
};

const statusLabel: Record<RoomStatus, string> = {
  live: 'Ao vivo',
  soon: 'Em breve',
  reprise: 'Reprise',
};

type EventInfoCardProps = {
  roomsQuery: UseQueryResult<RoomsWithSpeakersResult, AxiosError>;
};

const EventInfoCard = ({ roomsQuery }: EventInfoCardProps) => {
  const [expanded, setExpanded] = useState(false);
  const [needsExpand, setNeedsExpand] = useState(false);
  const descRef = useRef<HTMLParagraphElement>(null);
  const event = useEventfy();
  const history = useHistory();
  const { t } = useTranslation();

  useEffect(() => {
    const el = descRef.current;
    if (el) {
      setNeedsExpand(el.scrollHeight > el.clientHeight);
    }
  }, [event.description]);

  const mainRoom = roomsQuery.data?.rooms[0];
  const roomStatus: RoomStatus = mainRoom ? getRoomStatus(mainRoom) : 'soon';
  const live = roomStatus === 'live';

  const handleStageClick = () => {
    if (mainRoom) {
      history.push(`/broadcast/${mainRoom.slug}`);
    }
  };

  return (
    <S.Card>
      <S.MainStageWrapper onClick={handleStageClick}>
        {!roomsQuery.isSuccess ? (
          <Skeleton width="100%" height="100%" />
        ) : (
          <>
            {mainRoom?.thumb_image && (
              <S.MainStageImage
                src={mainRoom.thumb_image}
                alt={mainRoom.name}
              />
            )}

            <S.StatusTag $variant={roomStatus}>
              {live && <S.LivePulse />}
              {statusLabel[roomStatus]}
            </S.StatusTag>

            <S.PlayCenter>
              <FaPlay size={20} />
            </S.PlayCenter>

            <S.GlassBar>
              <S.GlassPlayIcon>
                <FaPlay size={12} />
              </S.GlassPlayIcon>
              <S.GlassBarInfo>
                <S.GlassBarName>
                  {mainRoom?.name || t('pages.lobby.mainRoom')}
                </S.GlassBarName>
                <S.GlassBarSub>
                  {live && (
                    <>
                      <FiRadio
                        size={10}
                        style={{ marginRight: 4, verticalAlign: 'middle' }}
                      />
                      Acontecendo agora
                    </>
                  )}
                  {!live && 'Em breve'}
                </S.GlassBarSub>
              </S.GlassBarInfo>
              <S.GlassEnterBtn type="button">Assistir</S.GlassEnterBtn>
            </S.GlassBar>
          </>
        )}
      </S.MainStageWrapper>

      <S.TitleRow>
        <h1>{event.name}</h1>
      </S.TitleRow>

      <S.Description $expanded={expanded}>
        <p
          ref={descRef}
          dangerouslySetInnerHTML={{ __html: event.description }}
        />
        {!expanded && needsExpand && <S.DescriptionFade />}
      </S.Description>

      {needsExpand && (
        <S.ReadMoreButton onClick={() => setExpanded(!expanded)}>
          {expanded ? t('pages.lobby.aboutLess') : t('pages.lobby.aboutMore')}
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            style={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s ease',
            }}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </S.ReadMoreButton>
      )}
    </S.Card>
  );
};

export default memo(EventInfoCard);
